import type { CheckoutPath } from '../checkout/types'
import type { GutHealthBarPaymentType } from '../gutHealthBar/utils'
import { hashEmail } from '../utils/cryptoUtils'
import { getQuizUserId, sendGtmEvent } from './gtmUtils'

// Stripe lookup keys look like `GB_BERRY_12_BARS_1_MONTH`; the flavour code is
// the segment before the box count. Lets PDP and checkout events share the same
// `item_category2`, since `product_name` isn't populated on PDP prices.
export const getBarsFlavourFromLookupKey = (lookupKey: string): string =>
  lookupKey.match(/_([A-Z]+)_\d+_BARS_/)?.[1]?.toLowerCase() ?? ''

export const mapBarsItems = (items: BarsPlanItem[]) =>
  items.map((item) => ({
    ...item,
    item_id: 'gut_health_bar',
    item_list_name: 'PDP',
    item_name: 'Gut health bar',
    item_brand: 'ZOE',
    item_category: 'Gut Health Bars',
  }))

type TrackPDPViewEventProps = {
  currency: string
  user_id?: string
  user_email?: string
}

export const trackBarsPDPViewEvent = ({ currency, user_id, user_email }: TrackPDPViewEventProps) => {
  const event = {
    event: 'view_item',
    section: 'pdp',
    page_name: 'pdp',
    product_type: 'gut-health-bar',
    product_selected: `gut-health-bar`,
    ecommerce: {
      currency: currency?.toUpperCase(),
      items: [
        {
          item_id: 'gut_health_bar',
          item_name: 'Gut health bar',
          item_brand: 'ZOE',
          item_category: 'Gut Health Bars',
        },
      ],
    },
    ...((user_id || user_email) && { user_id: user_id || hashEmail(user_email) }),
  }

  sendGtmEvent(event)
}

const FREQUENCY_LABELS: Record<GutHealthBarPaymentType, string> = {
  '1_MONTH': 'Regular delivery',
  ONE_TIME: 'One time purchase',
}

type TrackBarsFrequencySelectedEventProps = {
  paymentType: GutHealthBarPaymentType
  user_email?: string
}

export const trackBarsFrequencySelectedEvent = ({ paymentType, user_email }: TrackBarsFrequencySelectedEventProps) => {
  const event = {
    event: 'user_click',
    click_type: 'cta',
    section: 'pdp',
    page_name: 'gut health bar',
    link_position: 'page_body',
    link_text_image: FREQUENCY_LABELS[paymentType],
    ...(user_email && { user_id: hashEmail(user_email) }),
  }

  sendGtmEvent(event)
}

export const trackBarsCarouselClick = (text: string, index: number) => {
  const userId = getQuizUserId()
  // Append the 1-based position so analytics can see how far into the carousel a
  // user got; fall back to a generic label when the text is empty (e.g. an image with no alt).
  const label = text.trim() || 'image'
  sendGtmEvent({
    event: 'user_click',
    click_type: 'carousel',
    section: 'pdp',
    page_name: 'gut health bar',
    link_position: 'page_body',
    link_text_image: `${label} (${index + 1})`,
    ...(userId && { user_id: userId }),
  })
}

export const trackBarsCtaClick = (label: string) => {
  const userId = getQuizUserId()
  sendGtmEvent({
    event: 'user_click',
    click_type: 'cta',
    section: 'pdp',
    page_name: 'gut health bar',
    link_position: 'page_body',
    link_text_image: label,
    ...(userId && { user_id: userId }),
  })
}

export const getDaily30BarsBundle = (countryCode: string) => `${countryCode}_DAILY30_BARS_WELCOME`

export type BarsPlanItem = {
  item_bundle?: string
  item_category2: string
  item_category3?: string
  item_category4: string
  price: number
  discount?: number
  quantity: number
}

type TrackBarsProductAddedEventProps = {
  currency: string
  items: BarsPlanItem[]
  user_email?: string
  page_name?: string
}

export const trackBarsProductAddedEvent = ({
  currency,
  items,
  user_email,
  page_name = 'pdp',
}: TrackBarsProductAddedEventProps) => {
  const event = {
    event: 'add_to_cart',
    section: 'pdp',
    page_name,
    product_type: 'gut-health-bar',
    product_selected: `gut-health-bar-${items[0].item_category2}`,
    ecommerce: {
      currency: currency?.toUpperCase(),
      items: mapBarsItems(items),
    },
    ...(user_email && { user_id: hashEmail(user_email) }),
  }

  sendGtmEvent(event)
}

type TrackBarsProductRemovedEventProps = {
  currency: string
  items: BarsPlanItem[]
  user_email?: string
  page_name?: string
}

export const trackBarsProductRemovedEvent = ({
  currency,
  items,
  user_email,
  page_name = 'pdp',
}: TrackBarsProductRemovedEventProps) => {
  const event = {
    event: 'remove_from_cart',
    section: 'pdp',
    page_name,
    product_type: 'gut-health-bar',
    product_selected: `gut-health-bar-${items[0].item_category2}`,
    ecommerce: {
      currency: currency?.toUpperCase(),
      items: mapBarsItems(items),
    },
    ...(user_email && { user_id: hashEmail(user_email) }),
  }

  sendGtmEvent(event)
}

type TrackSelectedEventProps = {
  currency: string
  items: BarsPlanItem[]
  user_id?: string
  user_email?: string
}

export const trackSelectedEvent = ({ currency, items, user_email }: TrackSelectedEventProps) => {
  const event = {
    event: 'view_cart',
    section: 'checkout',
    page_name: 'pdp',
    product_type: 'gut-health-bar',
    product_selected: `gut-health-bar`,
    ecommerce: {
      currency: currency?.toUpperCase(),
      items: mapBarsItems(items),
    },
    ...(user_email && { user_id: hashEmail(user_email) }),
  }

  sendGtmEvent(event)
}

type TrackBeginBarsCheckoutEventProps = {
  user_type: 'New' | 'Existing' | 'Guest'
  currency: string
  items: BarsPlanItem[]
  user_id?: string
  user_email?: string
  checkout_path?: CheckoutPath
}

export const trackBeginBarsCheckoutEvent = ({
  user_type,
  currency,
  items,
  user_email,
  checkout_path,
}: TrackBeginBarsCheckoutEventProps) => {
  const event = {
    event: 'begin_checkout',
    user_type: user_type,
    section: 'checkout',
    page_name: 'checkout',
    ...(checkout_path && { checkout_path }),
    product_type: 'gut-health-bar',
    product_selected: `gut-health-bar`,
    ecommerce: {
      currency: currency?.toUpperCase(),
      items: mapBarsItems(items),
    },
    ...(user_email && { user_id: hashEmail(user_email) }),
  }

  sendGtmEvent(event)
}

type TrackBarsQuantityChangeEventProps = {
  flavour: string
  quantityFrom: number
  quantityTo: number
  user_email?: string
}

export const trackBarsQuantityChangeEvent = ({
  flavour,
  quantityFrom,
  quantityTo,
  user_email,
}: TrackBarsQuantityChangeEventProps) => {
  const event = {
    event: 'gut_health_bar_quantity_change',
    section: 'account',
    page_name: 'change_subscription',
    product_type: 'gut-health-bar',
    product_selected: `gut-health-bar-${flavour}`,
    quantity_from: quantityFrom,
    quantity_to: quantityTo,
    ...(user_email && { user_id: hashEmail(user_email) }),
  }

  sendGtmEvent(event)
}

type UserData = {
  first_name: string
  last_name: string
  email: string
  phone_number: string
  country: string
  city: string
  postal_code: string
}

type TrackBarsPurchaseEventProps = {
  user_type: 'New' | 'Existing' | 'Guest'
  currency: string
  tax?: number
  shipping?: number
  coupon?: string
  transaction_id: string
  value: number
  shipping_tier?: string
  payment_type: string
  items: BarsPlanItem[]
  user_data: UserData
  user_email?: string
  checkout_path?: CheckoutPath
}

export const trackBarsPurchaseEvent = ({
  user_email,
  user_type,
  currency,
  tax,
  shipping,
  coupon,
  transaction_id,
  value,
  shipping_tier,
  payment_type,
  items,
  user_data,
  checkout_path,
}: TrackBarsPurchaseEventProps) => {
  const event = {
    event: 'purchase',
    user_type: user_type,
    section: 'checkout',
    page_name: 'purchase',
    ...(checkout_path && { checkout_path }),
    product_type: 'gut-health-bar',
    product_selected: `gut-health-bar`,
    ecommerce: {
      currency: currency?.toUpperCase(),
      tax,
      coupon,
      transaction_id,
      value,
      payment_type,
      items: mapBarsItems(items),
      ...(shipping && { shipping }),
      ...(shipping_tier && { shipping_tier }),
    },
    user_data,
    ...(user_email && { user_id: hashEmail(user_email) }),
  }

  sendGtmEvent(event)
}
